import React from 'react'
import { Button, Card, DatePicker, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from 'antd'
import dayjs from 'dayjs'
import { listGeneralConfigs, createGeneralConfig, updateGeneralConfig, deleteGeneralConfig } from '../../api/generalConfig'

const { Title, Text } = Typography

function toIso(d) {
    return d ? d.toISOString() : null
}

function fromIso(s) {
    return s ? dayjs(s) : null
}

export default function GeneralConfigAdmin() {
    const [data, setData] = React.useState([])
    const [loading, setLoading] = React.useState(false)
    const [createOpen, setCreateOpen] = React.useState(false)
    const [creating, setCreating] = React.useState(false)
    const [editOpen, setEditOpen] = React.useState(false)
    const [editing, setEditing] = React.useState(false)
    const [selected, setSelected] = React.useState(null)

    const [createForm] = Form.useForm()
    const [editForm] = Form.useForm()

    const fetchData = React.useCallback(async () => {
        setLoading(true)
        try {
            const items = await listGeneralConfigs()
            setData(items || [])
        } catch (e) {
            console.error(e)
            message.error(e.message || 'فشل في جلب البيانات')
        } finally {
            setLoading(false)
        }
    }, [])

    React.useEffect(() => { fetchData() }, [fetchData])

    async function onCreate(values) {
        setCreating(true)
        try {
            await createGeneralConfig({
                key: values.key,
                value: values.value ?? null,
                effectiveStart: toIso(values.effectiveStart),
                effectiveEnd: toIso(values.effectiveEnd)
            })
            message.success('تم الإنشاء')
            setCreateOpen(false)
            createForm.resetFields()
            await fetchData()
        } catch (e) {
            message.error(e.message)
        } finally {
            setCreating(false)
        }
    }

    function openEdit(record) {
        setSelected(record)
        editForm.setFieldsValue({
            key: record.key,
            value: record.value,
            effectiveStart: fromIso(record.effectiveStart),
            effectiveEnd: fromIso(record.effectiveEnd)
        })
        setEditOpen(true)
    }

    async function onEdit(values) {
        if (!selected) return
        setEditing(true)
        try {
            await updateGeneralConfig(selected.id, {
                key: values.key,
                value: values.value ?? null,
                effectiveStart: toIso(values.effectiveStart),
                effectiveEnd: toIso(values.effectiveEnd)
            })
            message.success('تم الحفظ')
            setEditOpen(false)
            setSelected(null)
            await fetchData()
        } catch (e) {
            message.error(e.message)
        } finally {
            setEditing(false)
        }
    }

    async function onDelete(id) {
        try {
            await deleteGeneralConfig(id)
            message.success('تم الحذف')
            await fetchData()
        } catch (e) {
            message.error(e.message)
        }
    }

    const showIds = React.useMemo(() => {
        try { return localStorage.getItem('showIds') === 'true' } catch { return false }
    }, [])

    const columns = [
        ...(showIds ? [{ title: 'ID', dataIndex: 'id', key: 'id', width: 80 }] : []),
        {
            title: 'المفتاح', dataIndex: 'key', key: 'key',
            render: (v) => <Text code>{v}</Text>
        },
        {
            title: 'القيمة', dataIndex: 'value', key: 'value',
            render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text>
        },
        {
            title: 'بداية الصلاحية', dataIndex: 'effectiveStart', key: 'effectiveStart',
            render: (v) => v ? new Date(v).toLocaleString('ar-EG') : <Text type="secondary">—</Text>
        },
        {
            title: 'نهاية الصلاحية', dataIndex: 'effectiveEnd', key: 'effectiveEnd',
            render: (v) => v ? new Date(v).toLocaleString('ar-EG') : <Text type="secondary">—</Text>
        },
        {
            title: '', key: 'actions', width: 180,
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(record)}>تعديل</Button>
                    <Popconfirm
                        title="حذف هذا الإعداد؟"
                        okText="حذف"
                        cancelText="إلغاء"
                        onConfirm={() => onDelete(record.id)}
                    >
                        <Button size="small" danger>حذف</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ]

    return (
        <div style={{ maxWidth: 1200, margin: '40px auto', padding: '0 16px', direction: 'rtl' }}>
            <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>الإعدادات العامة</Title>
                <Space>
                    <Button onClick={fetchData} loading={loading}>تحديث</Button>
                    <Button type="primary" onClick={() => setCreateOpen(true)}>إضافة إعداد</Button>
                </Space>
            </Space>

            <Card bodyStyle={{ padding: 0 }}>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    pagination={{ pageSize: 25 }}
                    scroll={{ x: 800 }}
                />
            </Card>

            <Modal
                title="إضافة إعداد"
                open={createOpen}
                onCancel={() => { setCreateOpen(false); createForm.resetFields() }}
                onOk={() => createForm.submit()}
                confirmLoading={creating}
                okText="إضافة"
                cancelText="إلغاء"
                destroyOnClose
            >
                <Form form={createForm} layout="vertical" onFinish={onCreate}>
                    <Form.Item label="المفتاح (config_key)" name="key" rules={[{ required: true, message: 'مطلوب' }]}>
                        <Input placeholder="مثال: driver_fees_enabled" />
                    </Form.Item>
                    <Form.Item label="القيمة" name="value">
                        <Input.TextArea rows={3} placeholder="مثال: true" />
                    </Form.Item>
                    <Form.Item label="بداية الصلاحية (اختياري)" name="effectiveStart">
                        <DatePicker showTime style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="نهاية الصلاحية (اختياري)" name="effectiveEnd">
                        <DatePicker showTime style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="تعديل إعداد"
                open={editOpen}
                onCancel={() => { setEditOpen(false); setSelected(null) }}
                onOk={() => editForm.submit()}
                confirmLoading={editing}
                okText="حفظ"
                cancelText="إلغاء"
                destroyOnClose
            >
                <Form form={editForm} layout="vertical" onFinish={onEdit}>
                    <Form.Item label="المفتاح (config_key)" name="key" rules={[{ required: true, message: 'مطلوب' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="القيمة" name="value">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="بداية الصلاحية (اختياري)" name="effectiveStart">
                        <DatePicker showTime style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="نهاية الصلاحية (اختياري)" name="effectiveEnd">
                        <DatePicker showTime style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}
